/**
 * Network activity tracker. Every request made through the shared axios client registers here so
 * the UI can show the brand progress while data is loading (reads) or being saved (writes).
 *
 * `trackActivity(client)` must be called AFTER the client's retry/refresh interceptors are
 * registered: interceptors run in registration order, so tracking last means a request that is
 * retried stays "active" until its final outcome. Ids make `end` idempotent for replayed configs.
 * Pass `{ silent: true }` in a request config for background polling that should never show progress.
 */
const active = new Map();
const listeners = new Set();
let sequence = 0;
let locals = 0;
let snapshot = { pending: 0, writing: 0, locals: 0 };

const publish = () => {
  const writing = [...active.values()].filter(Boolean).length;
  if (snapshot.pending !== active.size || snapshot.writing !== writing || snapshot.locals !== locals) {
    snapshot = { pending: active.size, writing, locals };
    listeners.forEach((listener) => listener());
  }
};

const isWrite = (method) => !['get', 'head', 'options'].includes(String(method || 'get').toLowerCase());

export const activity = {
  begin(write) {
    sequence += 1;
    const id = `a${sequence}`;
    active.set(id, Boolean(write));
    publish();
    return id;
  },
  end(id) {
    if (id && active.delete(id)) publish();
  },
  /**
   * A screen-level loader (BrandSpinner, BrandedLoader, Suspense fallback) is on screen. While one
   * is mounted the global glass loader stays hidden, so a load never shows two brand loaders.
   * Returns the unmount function.
   */
  mountLocal() {
    locals += 1;
    publish();
    let released = false;
    return () => {
      if (released) return;
      released = true;
      locals -= 1;
      publish();
    };
  },
  subscribe(listener) {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
  getSnapshot: () => snapshot,
};

export const trackActivity = (client) => {
  client.interceptors.request.use((config) => {
    if (!config.silent && !config.__activityId) config.__activityId = activity.begin(isWrite(config.method));
    return config;
  });
  client.interceptors.response.use(
    (response) => {
      activity.end(response.config?.__activityId);
      return response;
    },
    (error) => {
      activity.end(error.config?.__activityId);
      throw error;
    },
  );
  return client;
};
